import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavStack } from '@/hooks/useNavLayer';
import {
  NoticeController,
  type ProtectionNoticeInput,
  type UndoNoticeInput,
} from './notice-controller';
import styles from './DeletionNotices.module.css';

/**
 * Delete Undo pill + protection messages, rendered INSIDE the surface the
 * user is looking at (garden-remember readiness P1, 2026-10-15).
 *
 * Before: both notices were fixed children of PrayPage's container at z 400,
 * so every NavStack overlay (z 1000+) painted over them, and the full-width
 * protection card painted over the Undo pill at the same bottom position.
 *
 * Now each notice resolves its own host: the topmost `aria-modal` dialog, or
 * PrayPage's home container when none is open. A deletion's host is chosen
 * only after the deleting dialogs (the prayer and anything above it) have
 * actually unmounted; a standalone notice resolves on the next frame.
 *
 * Inside a dialog the notices share one bottom region (Undo nearest the
 * thumb). On home they go into HomeFeedbackArea's slots (`homeSlots`): Undo
 * in its pinned row, a failure at the head of its scroll column, a pending /
 * preparing message at its tail, so recovery UI between them is never
 * covered. Without slots (not mounted yet) home falls back to the bottom
 * region. A dialog opened later stacks above its host and covers the notices
 * instead of having them painted over its own controls.
 *
 * Protection messages come in two channels (2026-10-15 amendment): `info`
 * (tap dismisses; PrayPage expires it after 6 s) and `failure` (an attention
 * card with an explicit Dismiss button; it does not expire).
 *
 * PrayPage keeps the 7 s Undo timer. NoticeController reports one boolean:
 * Undo is paused while hovered, focused, or covered (including while its host
 * is still being chosen). PrayPage restarts the full 7 s only when every
 * reason clears.
 */

export interface HomeNoticeSlots {
  undo: HTMLElement;
  head: HTMLElement;
  tail: HTMLElement;
}

interface DeletionNoticesProps {
  homeRef: RefObject<HTMLElement | null>;
  homeSlots: HomeNoticeSlots | null;
  undo: UndoNoticeInput | null;
  info: ProtectionNoticeInput | null;
  failure: ProtectionNoticeInput | null;
  onUndo: () => void;
  onDismissInfo: () => void;
  onDismissFailure: () => void;
  onUndoPausedChange: (paused: boolean) => void;
}

let nextTargetKey = 1;
const targetKeys = new WeakMap<HTMLElement, string>();
function keyForTarget(target: HTMLElement): string {
  let key = targetKeys.get(target);
  if (key === undefined) {
    key = `notice-target-${nextTargetKey}`;
    nextTargetKey += 1;
    targetKeys.set(target, key);
  }
  return key;
}

interface Placement {
  target: HTMLElement;
  host: HTMLElement;
  slot: boolean;
  nodes: ReactNode[];
}

export default function DeletionNotices({
  homeRef,
  homeSlots,
  undo,
  info,
  failure,
  onUndo,
  onDismissInfo,
  onDismissFailure,
  onUndoPausedChange,
}: DeletionNoticesProps) {
  const { t } = useTranslation();
  const nav = useNavStack();
  const [controller] = useState(() => new NoticeController());
  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot);

  useEffect(() => {
    controller.setInputs({ home: homeRef, undo, info, failure, onUndoPausedChange });
  }, [controller, homeRef, undo, info, failure, onUndoPausedChange]);

  useEffect(() => nav.subscribe(controller.layersChanged), [nav, controller]);
  useEffect(() => () => controller.dispose(), [controller]);

  const { undoHost, infoHost, failureHost, home } = snapshot;
  const placements: Placement[] = [];
  const place = (host: HTMLElement, homeSlot: keyof HomeNoticeSlots, node: ReactNode) => {
    const slot = host === home && homeSlots !== null;
    const target = slot ? homeSlots[homeSlot] : host;
    let placement = placements.find((entry) => entry.target === target);
    if (!placement) {
      placement = { target, host, slot, nodes: [] };
      placements.push(placement);
    }
    placement.nodes.push(node);
  };

  // DOM order inside a shared (dialog) region: Undo, failure, info.
  if (undo && undoHost) {
    place(undoHost, 'undo', (
      <UndoPill
        key="undo"
        host={undoHost}
        controller={controller}
        ready={snapshot.undoReady}
        reasons={snapshot.reasons.join(' ')}
        message={t('pray.undoDeleteMessage')}
        action={t('pray.undoDeleteAction')}
        onUndo={onUndo}
      />
    ));
  }
  if (failure && failureHost) {
    place(failureHost, 'head', (
      <FailureCard
        key="failure"
        host={failureHost}
        controller={controller}
        event={failure.event}
        ready={snapshot.failureReady}
        message={failure.message}
        dismissLabel={t('pray.dismissNoticeAria')}
        onDismiss={onDismissFailure}
      />
    ));
  }
  if (info && infoHost) {
    place(infoHost, 'tail', (
      <div
        key="info"
        className={`${styles.toast} ${styles.protection}`}
        role="status"
        aria-live="polite"
        data-notice="protection"
        onClick={onDismissInfo}
      >
        {snapshot.infoReady && <span className={styles.message}>{info.message}</span>}
      </div>
    ));
  }

  return (
    <>
      {placements.map(({ target, host, slot, nodes }) =>
        createPortal(
          <div
            className={slot ? styles.slotRegion : styles.region}
            data-deletion-notices
            data-host-home={host === home ? 'true' : 'false'}
          >
            {nodes}
          </div>,
          target,
          keyForTarget(target),
        ),
      )}
    </>
  );
}

interface UndoPillProps {
  host: HTMLElement;
  controller: NoticeController;
  ready: boolean;
  reasons: string;
  message: string;
  action: string;
  onUndo: () => void;
}

function UndoPill({ host, controller, ready, reasons, message, action, onUndo }: UndoPillProps) {
  const ref = useRef<HTMLDivElement>(null);
  // Layout cleanup runs before React detaches the DOM, so focus can still be
  // observed inside the pill here (browsers send no blur for a removed node).
  useLayoutEffect(() => {
    const element = ref.current;
    return () => {
      if (element && element.contains(document.activeElement)) {
        controller.focusedUndoRemoved(host);
      }
    };
  }, [controller, host]);

  return (
    <div
      ref={ref}
      className={`${styles.toast} ${styles.undo}`}
      role="status"
      aria-live="assertive"
      data-notice="undo"
      data-undo-paused={reasons}
      onMouseEnter={() => controller.setHovered(true)}
      onMouseLeave={() => controller.setHovered(false)}
      onFocus={() => controller.setFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          controller.setFocused(false);
        }
      }}
    >
      {ready && (
        <>
          <span className={styles.message}>{message}</span>
          <button type="button" className={styles.action} onClick={onUndo}>
            {action}
          </button>
        </>
      )}
    </div>
  );
}

interface FailureCardProps {
  host: HTMLElement;
  controller: NoticeController;
  event: number;
  ready: boolean;
  message: string;
  dismissLabel: string;
  onDismiss: () => void;
}

/**
 * Failure / attention message: same copy and live-region role as before, an
 * icon so colour is not the only cue, and a 44×44 Dismiss button. It stays
 * until dismissed or replaced (no timer).
 */
function FailureCard({ host, controller, event, ready, message, dismissLabel, onDismiss }: FailureCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const element = ref.current;
    return () => {
      if (element && element.contains(document.activeElement)) {
        controller.focusedNoticeRemoved(host);
      }
    };
  }, [controller, host]);

  return (
    <div
      ref={ref}
      className={`${styles.toast} ${styles.protection} ${styles.failure}`}
      role="status"
      aria-live="polite"
      data-notice="failure"
      data-notice-event={event}
    >
      {ready && (
        <>
          <svg className={styles.failureIcon} viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
            <circle cx="12" cy="12" r="9.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
            <path d="M12 7.5v5.5M12 16.4v.1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <span className={`${styles.message} ${styles.failureMessage}`}>{message}</span>
          <button type="button" className={styles.dismiss} aria-label={dismissLabel} onClick={onDismiss}>
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </>
      )}
    </div>
  );
}
